<template>
    <div class="qwen-image-page">
        <!-- Image Generator Section -->
        <section ref="generatorSection" class="generator-section">
            <div class="container">
                <div class="generator-header">
                    <h1 class="generator-title">
                        {{ t('qwenImage.generatorTitle') }}
                    </h1>
                    <p class="generator-subtitle">
                        {{ t('qwenImage.generatorSubtitle') }}
                    </p>
                </div>

                <ImageGenerator model-name="Qwen Image" :default-tab="1" @generate="handleImageGenerate"
                    model-key="QWEN" />
            </div>
        </section>

        <!-- Core Features Section -->
        <section class="core-features-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('qwenImage.coreFeaturesTitle') }}
                    </h2>
                    <p class="section-subtitle">
                        {{ t('qwenImage.coreFeaturesSubtitle') }}
                    </p>
                </div>

                <div class="features-showcase-container">
                    <!-- Diverse Image Generation Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('qwenImage.diverseImageTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('qwenImage.diverseImageDesc') }}
                                </p>
                            </div>
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/8c63902ea87f7a87a733d6f54776a980.webp"
                                        :alt="t('qwenImage.diverseImageAlt')" class="feature-image-preview"
                                        fetchpriority="high" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Intuitive Image Editing Feature (reverse layout) -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/c741ee77c891932068a86877770301c3.webp" :alt="t('qwenImage.intuitiveEditingAlt')
                                        " class="feature-image-preview" loading="lazy" />
                                </div>
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('qwenImage.intuitiveEditingTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('qwenImage.intuitiveEditingDesc') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Fine-grained Text Rendering Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('qwenImage.fineTextRenderingTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('qwenImage.fineTextRenderingDesc') }}
                                </p>
                            </div>
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/b0c4d3a014c3d40e76b91f8ac608a6d8.webp" :alt="t('qwenImage.fineTextRenderingAlt')
                                        " class="feature-image-preview" loading="lazy" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Smart Prompt Following Feature (reverse layout) -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/5002d857f05b07a2669482a1afd7a609.webp"
                                        :alt="t('qwenImage.smartPromptAlt')" class="feature-image-preview"
                                        loading="lazy" />
                                </div>
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('qwenImage.smartPromptTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('qwenImage.smartPromptDesc') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- How to Use Section -->
        <section class="how-to-use-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('qwenImage.howToUseTitle') }}
                    </h2>
                    <p class="section-subtitle">
                        {{ t('qwenImage.howToUseSubtitle') }}
                    </p>
                </div>

                <ol class="steps-container">
                    <li class="step-item">
                        <div class="step-number">1</div>
                        <h3 class="step-title">
                            {{ t('qwenImage.step1Title') }}
                        </h3>
                        <p class="step-description">
                            {{ t('qwenImage.step1Description') }}
                        </p>
                    </li>

                    <li class="step-item">
                        <div class="step-number">2</div>
                        <h3 class="step-title">
                            {{ t('qwenImage.step2Title') }}
                        </h3>
                        <p class="step-description">
                            {{ t('qwenImage.step2Description') }}
                        </p>
                    </li>

                    <li class="step-item">
                        <div class="step-number">3</div>
                        <h3 class="step-title">
                            {{ t('qwenImage.step3Title') }}
                        </h3>
                        <p class="step-description">
                            {{ t('qwenImage.step3Description') }}
                        </p>
                    </li>
                </ol>
            </div>
        </section>

        <!-- YouTube Videos Section -->
        <YouTubeCarouselSection :title="t('qwenImage.youtubeTitle')" :videos="youtubeVideos" />

        <!-- X Posts Section -->
        <TwitterPosts :title="t('qwenImage.xPostsTitle')" :tweet-urls="twitterTweetUrls" />

        <!-- Explore Other Models Section -->
        <section class="explore-models-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('qwenImage.exploreModelsTitle') }}
                    </h2>
                </div>

                <div class="models-grid">
                    <NuxtLink to="/image/nano-banana" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/3ed8c9e1a1ec3b37133c608c292821cb.png"
                                :alt="t('qwenImage.model1Title')" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('qwenImage.model1Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/image/flux" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/a4d5bf7d150f6738f42d3d628ac39869.png"
                                :alt="t('qwenImage.model2Title')" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('qwenImage.model2Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/image/imagen" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/8ec333d0fa59f3ebf4a2373592ae76a6.png"
                                :alt="t('qwenImage.model3Title')" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('qwenImage.model3Title') }}
                        </h3>
                    </NuxtLink>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="faq-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">{{ t('qwenImage.faqTitle') }}</h2>
                </div>

                <div class="faq-list">
                    <div v-for="(faq, index) in faqData" :key="index" class="faq-item"
                        :class="{ active: activeFaqIndex === index }">
                        <div class="faq-question" @click="toggleFaq(index)">
                            <!-- <span class="faq-icon">></span> -->
                            <span class="faq-text">{{ t(faq.question) }}</span>
                        </div>
                        <div class="faq-answer">
                            <p>{{ t(faq.answer) }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA Section -->
        <section class="cta-section">
            <div class="container">
                <div class="cta-content">
                    <!-- 微鲤AI Logo -->
                    <div class="cta-logo">
                        <div class="logo-icon logo-image">
                            <img src="https://static.weryai.com/growth/image/4608e0a35205ff929dba2ea3c51f6e62.webp"
                                alt="微鲤AI" class="logo-image" loading="lazy" />
                        </div>
                    </div>

                    <!-- Main Content -->
                    <div class="cta-header">
                        <h2 class="cta-title">{{ t('qwenImage.ctaTitle') }}</h2>
                        <p class="cta-description">
                            {{ t('qwenImage.ctaDescription') }}
                        </p>
                    </div>

                    <!-- CTA Button -->
                    <div class="cta-actions">
                        <button @click="scrollToGenerator" class="cta-primary-btn">
                            {{ t('qwenImage.ctaButton') }}
                        </button>

                        <p class="cta-disclaimer">
                            {{ t('qwenImage.ctaDisclaimer') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useHead, useRequestURL } from '#imports'
import ImageGenerator from '~/components/ImageGenerator.vue'
import TwitterPosts from '~/components/TwitterPosts.vue'
import YouTubeCarouselSection from '~/components/model/video/YouTubeCarouselSection.vue'
import { useI18n } from '~/composables/useI18n'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { analyticsEvent } from '~/utils/log-event-func'

const { t } = useI18n()
const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

definePageMeta({
    layout: 'website',
})

useMultilingualSeo({
    pageType: 'image_qwen_image',
})

// BreadcrumbList SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('qwenImage.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})

// Ref for generator section
const generatorSection = ref<HTMLElement | null>(null)

// Scroll to generator function
const scrollToGenerator = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

// YouTube Videos Data
const youtubeVideos = ref([
    {
        title: 'Qwen Image Video 1',
        embedUrl: 'https://www.youtube.com/embed/TH7mPjIO7XU',
    },
    {
        title: 'Qwen Image Video 2',
        embedUrl: 'https://www.youtube.com/embed/3-vLVJaliHI',
    },
    {
        title: 'Qwen Image Video 3',
        embedUrl: 'https://www.youtube.com/embed/WOcxMUwKWIk',
    },
    {
        title: 'Qwen Image Video 4',
        embedUrl: 'https://www.youtube.com/embed/JLX7x854Xq0',
    },
    {
        title: 'Qwen Image Video 5',
        embedUrl: 'https://www.youtube.com/embed/ex2YCqtTHSY',
    },
])

// Twitter Tweet URLs Data
const twitterTweetUrls = [
    'https://twitter.com/heyglif/status/1958193356883828967',
    'https://twitter.com/AskVenice/status/1957580817913311288',
    'https://twitter.com/bobduffy/status/1952900987049656691',
    'https://twitter.com/linoy_tsaban/status/1957518340106432600',
    'https://twitter.com/RisingSayak/status/1952400893586071629',
    'https://twitter.com/itsPaulAi/status/1957912985709531408',
    'https://twitter.com/AIWarper/status/1961450803077796162',
    'https://twitter.com/Saboo_Shubham_/status/1957630420394340470',
    'https://twitter.com/AIWarper/status/1961606350548451371',
    'https://twitter.com/paul_dentro/status/1952730835158745368',
    'https://twitter.com/wildmindai/status/1961123949124735389',
]

// HowTo SEO - 为"如何使用"步骤添加结构化数据
const howToSteps = computed(() => [
    {
        title: t('qwenImage.step1Title'),
        description: t('qwenImage.step1Description'),
    },
    {
        title: t('qwenImage.step2Title'),
        description: t('qwenImage.step2Description'),
    },
    {
        title: t('qwenImage.step3Title'),
        description: t('qwenImage.step3Description'),
    },
])

// 注入 HowTo 结构化数据（支持响应式）
useHowToSeo(
    computed(() => t('qwenImage.howToUseTitle')),
    howToSteps
)

// FAQ Data
const faqData = ref([
    {
        question: 'qwenImage.faq1Question',
        answer: 'qwenImage.faq1Answer',
    },
    {
        question: 'qwenImage.faq2Question',
        answer: 'qwenImage.faq2Answer',
    },
    {
        question: 'qwenImage.faq3Question',
        answer: 'qwenImage.faq3Answer',
    },
    {
        question: 'qwenImage.faq4Question',
        answer: 'qwenImage.faq4Answer',
    },
    {
        question: 'qwenImage.faq5Question',
        answer: 'qwenImage.faq5Answer',
    },
])

// FAQ Interactive Functions
const activeFaqIndex = ref<number | null>(null)

const toggleFaq = (index: number) => {
    if (activeFaqIndex.value === index) {
        activeFaqIndex.value = null
    } else {
        activeFaqIndex.value = index
    }
}

// Image Generation Handler
const handleImageGenerate = (data: {
    prompt: string
    image?: File
    tabType: 'text-to-image' | 'image-to-image'
}) => {
    console.log('Image generation data:', data)
    // Add actual image generation logic here
    // For example, call API, show loading state, etc
}

onMounted(() => {
    analyticsEvent('view_model_page_common', {
        model_name: 'qwen_image',
        type: 'image',
    })
})
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';
</style>
